import type {
  AccountFootprint,
  AssetInfo,
  AuthConfig,
  BoardDetail,
  BoardFile,
  BoardSummary,
  InvitePreview,
  InviteRole,
  InviteSummary,
  MemberInfo,
  NewInvite,
  Prefs,
  PublicUser,
} from '@scribbleplot/shared';

/**
 * Typed fetch wrapper.
 *
 * Every call is same-origin with credentials, so the session cookie flows
 * without any token juggling. Failures surface as `ApiError` carrying the
 * server's user-facing message, which the screens render directly.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      credentials: 'same-origin',
      ...init,
      headers: {
        ...(init.body && !(init.body instanceof FormData) ? { 'content-type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch {
    // Network-level failure: no response at all.
    throw new ApiError('Could not reach the server. Check your connection.', 0);
  }

  if (res.status === 204) return undefined as T;

  const body = await res.text();
  let parsed: unknown = null;
  if (body) {
    try {
      parsed = JSON.parse(body);
    } catch {
      parsed = null;
    }
  }

  if (!res.ok) {
    const message =
      (parsed && typeof parsed === 'object' && 'error' in parsed && typeof parsed.error === 'string'
        ? parsed.error
        : null) ?? `Request failed (${res.status})`;
    throw new ApiError(message, res.status);
  }
  return parsed as T;
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
const patch = <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });

export const api = {
  authConfig: () => request<AuthConfig>('/api/auth/config'),

  me: () => request<{ user: PublicUser }>('/api/me'),
  signup: (email: string, password: string, name?: string) =>
    post<{ user: PublicUser }>('/api/auth/signup', { email, password, name }),
  login: (email: string, password: string) => post<{ user: PublicUser }>('/api/auth/login', { email, password }),
  logout: () => post<{ ok: true }>('/api/auth/logout'),
  forgotPassword: (email: string) => post<{ ok: true }>('/api/auth/forgot', { email }),
  checkReset: (token: string) => request<{ email: string }>(`/api/auth/reset/${encodeURIComponent(token)}`),
  resetPassword: (token: string, password: string) =>
    post<{ user: PublicUser }>(`/api/auth/reset/${encodeURIComponent(token)}`, { password }),
  updateMe: (body: { name?: string; prefs?: Prefs }) => patch<{ user: PublicUser }>('/api/me', body),
  changePassword: (newPassword: string, currentPassword?: string) =>
    post<{ user: PublicUser }>('/api/me/password', { newPassword, currentPassword }),
  logoutEverywhere: () => post<{ ok: true }>('/api/auth/logout-all'),
  resendVerification: () => post<{ ok: true; alreadyVerified: boolean }>('/api/auth/verify/resend'),
  accountFootprint: () => request<{ footprint: AccountFootprint }>('/api/me/footprint'),
  deleteAccount: (confirm: string, password?: string) => post<{ ok: true }>('/api/me/delete', { confirm, password }),

  listBoards: () => request<{ boards: BoardSummary[] }>('/api/boards'),
  createBoard: (name?: string) => post<{ board: BoardSummary }>('/api/boards', { name }),
  getBoard: (id: string) =>
    request<{ board: BoardDetail; canEdit: boolean; canManage: boolean }>(`/api/boards/${encodeURIComponent(id)}`),
  getPublicBoard: (slug: string) =>
    request<{ board: BoardDetail; canEdit: boolean; canManage: boolean }>(`/api/public/${encodeURIComponent(slug)}`),
  updateBoard: (id: string, body: { name?: string; isPublic?: boolean }) =>
    patch<{ board: BoardDetail }>(`/api/boards/${encodeURIComponent(id)}`, body),
  exportBoard: (id: string) => request<BoardFile>(`/api/boards/${encodeURIComponent(id)}/export`),
  importBoard: (file: BoardFile) =>
    post<{ board: BoardSummary; images: { imported: number; skipped: number } }>('/api/boards/import', file),
  duplicateBoard: (id: string) => post<{ board: BoardSummary }>(`/api/boards/${encodeURIComponent(id)}/duplicate`),

  deleteBoard: (id: string) => request<{ ok: true }>(`/api/boards/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  /* --- collaborators --- */
  listCollaborators: (boardId: string) =>
    request<{ members: MemberInfo[]; invites: InviteSummary[] }>(`/api/boards/${encodeURIComponent(boardId)}/members`),
  addMember: (boardId: string, email: string, role: InviteRole) =>
    post<{ members: MemberInfo[] }>(`/api/boards/${encodeURIComponent(boardId)}/members`, { email, role }),
  setMemberRole: (boardId: string, userId: string, role: InviteRole) =>
    patch<{ members: MemberInfo[] }>(
      `/api/boards/${encodeURIComponent(boardId)}/members/${encodeURIComponent(userId)}`,
      { role },
    ),
  removeMember: (boardId: string, userId: string) =>
    request<{ members: MemberInfo[] }>(
      `/api/boards/${encodeURIComponent(boardId)}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE' },
    ),
  createInvite: (boardId: string, role: InviteRole) =>
    post<{ invite: NewInvite }>(`/api/boards/${encodeURIComponent(boardId)}/invites`, { role }),
  revokeInvite: (boardId: string, inviteId: string) =>
    request<{ invites: InviteSummary[] }>(
      `/api/boards/${encodeURIComponent(boardId)}/invites/${encodeURIComponent(inviteId)}`,
      { method: 'DELETE' },
    ),
  previewInvite: (token: string) => request<{ preview: InvitePreview }>(`/api/invites/${encodeURIComponent(token)}`),
  acceptInvite: (token: string) =>
    post<{ boardId: string; role: InviteRole; alreadyOwner: boolean }>(
      `/api/invites/${encodeURIComponent(token)}/accept`,
    ),

  listAssets: () => request<{ assets: AssetInfo[] }>('/api/assets'),
  deleteAsset: (id: string) => request<{ ok: true }>(`/api/assets/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  uploadAssets: async (files: File[], boardId?: string): Promise<AssetInfo[]> => {
    const form = new FormData();
    for (const file of files) form.append('file', file, file.name);
    const query = boardId ? `?boardId=${encodeURIComponent(boardId)}` : '';
    const { assets } = await request<{ assets: AssetInfo[] }>(`/api/assets${query}`, { method: 'POST', body: form });
    return assets;
  },
};

/** Absolute WebSocket URL for a board's realtime channel. */
export function boardSocketUrl(boardId: string): string {
  const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${location.host}/api/boards/${encodeURIComponent(boardId)}/socket`;
}
